import { UsState } from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { useFeatureFlagPayload } from 'posthog-js/react';

export enum FeatureKey {
  MAKE_OFFER = 'make-offer',
  OPEN_HOUSES = 'open-houses',
  OFFER_COACH = 'offer-coach',
  ORG_PIPELINE = 'org-pipeline',
  SYNC_KEY_TERMS_TO_DARWIN = 'sync-key-terms-to-darwin',
  KEY_TERMS_PDF = 'key-terms-pdf',
}

interface UseIsFeatureEnabledForPayloadProps {
  featureFlagKey: FeatureKey;
  mlsOSN?: string;
  state?: UsState;
  organizationId?: string;
  userId?: string;
}

/**
 * Example payload from PostHog feature flag:
 * {
 *   state: [UsState.CA, UsState.TX], // Array of US states where feature is enabled
 *   mls: ['CanopyMLS', 'TestMLS']    // Array of MLS originating system names where feature is enabled
 * }
 */

interface FeatureFlagPayload {
  state?: UsState[];
  mls?: string[];
  organizations?: string[];
  users?: string[];
  enabledForEveryone?: boolean;
}

export const useGetFeatureFlagPayload = (
  featureFlagKey: FeatureKey | undefined
) => {
  return useFeatureFlagPayload(featureFlagKey ?? '') as
    | FeatureFlagPayload
    | undefined;
};

export const useIsFeatureEnabledForPayload = ({
  featureFlagKey,
  ...rest
}: UseIsFeatureEnabledForPayloadProps) => {
  const payload = useGetFeatureFlagPayload(featureFlagKey);

  // make all features available in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (!payload) {
    // ! TODO: Handle this with feature flag bootstrapping:
    // ! https://posthog.com/tutorials/bootstrap-feature-flags-react
    // payload is still loading
    return undefined;
  }

  if (payload?.enabledForEveryone) {
    return true;
  }

  if (Object.values(rest).every((value) => isNil(value))) {
    return false;
  }

  const { state, mlsOSN, organizationId, userId } = rest;

  if (state && payload?.state?.includes(state)) {
    return true;
  }

  if (mlsOSN && payload?.mls?.includes(mlsOSN)) {
    return true;
  }

  if (organizationId && payload?.organizations?.includes(organizationId)) {
    return true;
  }

  if (userId && payload?.users?.includes(userId)) {
    return true;
  }

  return false;
};
